import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Navigation, MapPin, Clock, Phone, User } from 'lucide-react';
import { TrackingMap } from './TrackingMap';
import { LocationControls } from './LocationControls';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { LocationTracker, type LocationData, updateUserLocation } from '@/utils/locationTracker';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { toast } from 'sonner';

interface CustomerLocation {
  latitude: number;
  longitude: number;
  address?: string;
}

interface Job {
  id: string;
  title: string;
  location: string;
  status: string;
  customer_id: string;
  required_date?: string;
}

interface TradesmanTrackingDialogProps {
  job: Job | null;
  onClose: () => void;
}

export const TradesmanTrackingDialog: React.FC<TradesmanTrackingDialogProps> = ({ job, onClose }) => {
  const [customerLocation, setCustomerLocation] = useState<CustomerLocation | null>(null);
  const [currentLocation, setCurrentLocation] = useState<LocationData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [customerPhone, setCustomerPhone] = useState<string | null>(null);

  useEffect(() => {
    if (!job) return;

    console.log('TradesmanTrackingDialog: Fetching location for job:', job.id);

    const fetchCustomerData = async () => {
      try {
        setIsLoading(true);

        // Fetch customer phone number
        const { data: customerProfile, error: customerError } = await supabase
          .from('profiles')
          .select('phone')
          .eq('id', job.customer_id)
          .maybeSingle();

        if (customerProfile && !customerError) {
          setCustomerPhone(customerProfile.phone);
        }

        console.log('TradesmanTrackingDialog: Querying shared location via RPC for job:', job.id);
        // Use secure RPC function to get customer location
        const { data: locationData, error: locationError } = await supabase
          .rpc('get_shared_job_location', { p_job_id: job.id });
        
        const locationSharing = Array.isArray(locationData) ? locationData[0] : locationData;

        console.log('TradesmanTrackingDialog: Location sharing RPC result:', { 
          data: locationSharing, 
          error: locationError,
          hasLatitude: !!locationSharing?.customer_latitude,
          hasLongitude: !!locationSharing?.customer_longitude
        });

        if (locationSharing && locationSharing.customer_latitude && locationSharing.customer_longitude) {
          console.log('TradesmanTrackingDialog: ✓ Customer location found and set');
          setCustomerLocation({
            latitude: Number(locationSharing.customer_latitude),
            longitude: Number(locationSharing.customer_longitude),
          });
        } else {
          console.log('TradesmanTrackingDialog: ✗ No customer location shared', {
            hasData: !!locationSharing,
            hasCoordinates: !!(locationSharing?.customer_latitude && locationSharing?.customer_longitude)
          });
        }
      } catch (error) {
        console.error('TradesmanTrackingDialog: Error fetching customer location:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomerData();

    // Set up real-time subscription for customer location updates
    // Poll every 10 seconds for immediate updates
    const pollInterval = setInterval(async () => {
      try {
        console.log('TradesmanTrackingDialog: Polling for customer location update...');
        const { data: locationData } = await supabase
          .rpc('get_shared_job_location', { p_job_id: job.id });
        
        const locationSharing = Array.isArray(locationData) ? locationData[0] : locationData;
        
        if (locationSharing && locationSharing.customer_latitude && locationSharing.customer_longitude) {
          console.log('TradesmanTrackingDialog: Customer location update received');
          setCustomerLocation({
            latitude: Number(locationSharing.customer_latitude),
            longitude: Number(locationSharing.customer_longitude),
          });
        } else {
          console.log('TradesmanTrackingDialog: No customer location in poll');
        }
      } catch (error) {
        console.error('Error polling customer location:', error);
      }
    }, 10000); // Poll every 10 seconds for faster updates

    return () => {
      clearInterval(pollInterval);
    };
  }, [job]);

  const handleLocationUpdate = async (location: LocationData) => {
    setCurrentLocation(location);
    // Save to database so customer can see tradesman location
    try {
      await updateUserLocation(location);
      console.log('TradesmanTrackingDialog: Location saved to database for customer visibility');
    } catch (error) {
      console.error('TradesmanTrackingDialog: Failed to save location to database:', error);
    }
  };

  // Start lightweight background tracking (no UI) so your location appears on the map
  useEffect(() => {
    if (!job) return;
    const tracker = new LocationTracker();
    (async () => {
      try {
        await tracker.startTracking(handleLocationUpdate);
      } catch (e) {
        console.error('TradesmanTrackingDialog: Failed to start background tracking', e);
      }
    })();
    return () => {
      tracker.stopTracking();
    };
  }, [job]);

  const handleCallCustomer = async () => {
    if (!customerPhone) {
      toast.error('Customer phone number not available');
      return;
    }

    try {
      if (Capacitor.isNativePlatform()) {
        // For mobile app, use native phone dialer
        const info = await Device.getInfo();
        if (info.platform === 'ios' || info.platform === 'android') {
          // Open phone dialer with the customer's number
          window.open(`tel:${customerPhone}`, '_self');
        }
      } else {
        // For web, open phone dialer
        window.open(`tel:${customerPhone}`, '_self');
      }
    } catch (error) {
      console.error('Error making phone call:', error);
      toast.error('Unable to make phone call');
    }
  };

  if (!job) return null;

  const mapLocations = [
    ...(customerLocation ? [{
      latitude: customerLocation.latitude,
      longitude: customerLocation.longitude,
      title: 'Customer Location',
      description: 'Live customer location',
      type: 'customer' as const,
    }] : []),
    ...(currentLocation ? [{
      latitude: currentLocation.latitude,
      longitude: currentLocation.longitude,
      accuracy: currentLocation.accuracy,
      title: 'Your Location',
      description: 'Current position',
      type: 'tradesman' as const,
    }] : []),
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'assigned': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Dialog open={!!job} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Navigation className="h-5 w-5" />
            Job Navigation
          </DialogTitle>
          <DialogDescription>
            Navigate to customer location and share your arrival progress
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Job Info */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <h3 className="font-semibold">{job.title}</h3>
              <div className="flex items-center gap-1 text-muted-foreground text-sm">
                <MapPin className="h-4 w-4" />
                {customerLocation ? (
                  <span>Lat {customerLocation.latitude.toFixed(5)}, Lng {customerLocation.longitude.toFixed(5)}</span>
                ) : (
                  <span>Waiting for live customer location…</span>
                )}
              </div>
              {job.required_date && (
                <div className="flex items-center gap-1 text-muted-foreground text-sm">
                  <Clock className="h-4 w-4" />
                  <span>Scheduled: {format(new Date(job.required_date), 'PPP p')}</span>
                </div>
              )}
            </div>
            <Badge className={getStatusColor(job.status)} variant="secondary">
              {job.status.replace('_', ' ')}
            </Badge>
          </div>

          {/* Location tracking happens automatically in background */}

          {/* Navigation Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="h-4 w-4 text-primary" />
                <span className="font-medium">Customer Location</span>
              </div>
              {customerLocation ? (
                <div className="space-y-1 text-sm">
                  <div className="text-green-600 font-medium flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-600 animate-pulse"></span>
                    Available
                  </div>
                  {customerLocation.address && (
                    <div className="text-muted-foreground">
                      {customerLocation.address}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    Lat: {customerLocation.latitude.toFixed(5)}, Lng: {customerLocation.longitude.toFixed(5)}
                  </div>
                </div>
              ) : (
                <div className="space-y-1 text-sm">
                  <div className="text-amber-600 font-medium">
                    Waiting for customer location...
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Customer needs to enable location sharing
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-accent-orange" />
                <span className="font-medium">Your Tracking</span>
              </div>
              {currentLocation ? (
                <div className="text-sm text-green-600 font-medium">
                  Active - Customer can see your location
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Enable location sharing above
                </div>
              )}
            </div>
          </div>

          {/* Map */}
          <div className="space-y-2">
            <h4 className="font-medium">Navigation Map</h4>
            {isLoading ? (
              <div className="h-80 bg-muted rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading map...</p>
                </div>
              </div>
            ) : (
              <TrackingMap
                locations={mapLocations}
                className="h-80 w-full border rounded-lg"
                centerLat={customerLocation?.latitude || currentLocation?.latitude}
                centerLng={customerLocation?.longitude || currentLocation?.longitude}
                zoom={14}
                interactive={true}
                showNavigation={true}
                fitToLocations={mapLocations.length > 1}
              />
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button onClick={onClose} variant="outline" className="flex-1">
              Close Navigation
            </Button>
            {customerLocation && customerPhone && (
              <Button onClick={handleCallCustomer} variant="hero" className="flex-1">
                <Phone className="h-4 w-4 mr-2" />
                Call Customer
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};